import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Reveal } from "./Reveal";
import { STATS } from "../../data";

const formatNumber = (n) => n.toLocaleString("en-US");

const Counter = ({ target, suffix, display }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1800;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  return (
    <span ref={ref}>
      {value >= target ? display : `${formatNumber(value)}${suffix}`}
    </span>
  );
};

const CHAPTERS = [
  {
    n: "01",
    title: "Born in Cleveland",
    body: "SocialImpactCLE was founded to empower communities through programs that address real challenges — from rising costs and healthy eating to education, kindness, and recovery.",
  },
  {
    n: "02",
    title: "Built for the world",
    body: "What starts on our streets is designed to scale. Community-powered programs, practical tools, and a belief that impact belongs to everyone.",
  },
];

export const Mission = () => {
  return (
    <section
      id="about"
      data-testid="mission-section"
      className="relative bg-white py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand-teal mb-5 flex items-center gap-3">
                <span className="inline-block h-px w-10 bg-brand-teal" />
                Our Mission
              </p>
              <h2 className="font-display font-black tracking-tighter text-charcoal text-4xl md:text-6xl leading-[0.95]">
                Impact,
                <br />
                by <span className="text-brand-teal">design.</span>
              </h2>
            </Reveal>
          </div>

          <div className="lg:col-span-7 space-y-10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="flex gap-6 md:gap-8 border-b border-black/10 pb-9">
                  <span className="font-display font-black text-transparent text-5xl md:text-6xl leading-none shrink-0 [-webkit-text-stroke:1.5px_#2D2D2D22]">
                    {c.n}
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-xl md:text-2xl text-charcoal mb-3">
                      {c.title}
                    </h3>
                    <p className="text-charcoal/65 text-lg leading-relaxed max-w-xl">
                      {c.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Impact stats */}
        <div className="mt-20 md:mt-28 grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 rounded-3xl overflow-hidden border border-black/10">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={`stat-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="bg-warmgray p-8 md:p-10 group hover:bg-navy transition-colors duration-500"
            >
              <p className="font-display font-black tracking-tighter text-4xl md:text-5xl text-charcoal group-hover:text-brand-golden transition-colors duration-500">
                <Counter target={s.value} suffix={s.suffix} display={s.display} />
              </p>
              <p className="mt-3 font-mono text-xs uppercase tracking-widest text-charcoal/55 group-hover:text-white/70 transition-colors duration-500">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
